import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Avatar,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Add, Edit, Delete, PhotoCamera } from '@mui/icons-material';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useProducts, type ProductSummary } from '@/contexts/ProductsContext';
import { apiService } from '@/services/api';
import { PRICING_SCHEMA_REGISTRY } from '@/components/platform/pricingSchemas/registry';
import { useMessage } from '@/contexts/MessageContext';
import { ProductCell } from '@/components/ProductCell';
import AvatarEditor from '@/components/AvatarEditor';

interface ProductRow extends ProductSummary {
  displayName: string;
}

const emptyForm = {
  displayName: '',
  avatarUrl: '',
  productUrl: '',
  status: 'active' as ProductRow['status'],
  type: 'saas_web' as ProductRow['type'],
  appApi: '',
  appApiSecret: '',
  appApiSecretConfigured: false,
  billingEnabled: true,
  isFree: false,
  pricingSchema: '' as string
};

export const ProductRegistryTab: React.FC = () => {
  const { setRefreshCallback } = useRefresh();
  const { products, refreshProducts } = useProducts();
  const { can } = useAdminPermissions();
  const { showError, showSuccess, showWarning } = useMessage();
  const { confirm } = useConfirmation();
  const [testingBilling, setTestingBilling] = useState<string | null>(null);
  const [testingApi, setTestingApi] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [checklist, setChecklist] = useState<string[]>([]);
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      await refreshProducts();
    } catch {
      showError('Erro ao carregar produtos');
    }
  }, [refreshProducts, showError]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  useEffect(() => {
    setRefreshCallback(load);
    return () => setRefreshCallback(null);
  }, [load, setRefreshCallback]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setChecklist([]);
    setDialogOpen(true);
  };

  const openEdit = (p: ProductSummary) => {
    setEditingId(p.productId);
    setForm({
      displayName: p.displayName || '',
      avatarUrl: p.avatarUrl?.startsWith('data:image/') ? p.avatarUrl : '',
      productUrl: p.productUrl || '',
      status: (p.status as typeof emptyForm.status) || 'active',
      type: (p.type as typeof emptyForm.type) || 'saas_web',
      appApi: p.urls?.appApi || '',
      appApiSecret: '',
      appApiSecretConfigured: p.urls?.appApiSecretConfigured === true,
      billingEnabled: p.integrations?.billingEnabled !== false,
      isFree: p.isFree === true,
      pricingSchema: p.integrations?.pricingSchema ?? ''
    });
    setChecklist([]);
    setDialogOpen(true);
  };

  const runTest = async (productId: string, target: 'billing' | 'appApi') => {
    if (target === 'billing') setTestingBilling(productId);
    else setTestingApi(productId);
    try {
      const res = await apiService.testProductConnection(productId);
      const part = target === 'billing' ? res.data?.billing : res.data?.appApi;
      const label = target === 'billing' ? 'Billing' : 'API do produto';
      const msg = `${label}: ${part?.ok ? 'OK' : 'falha'} — ${part?.detail || ''}`;
      if (part?.ok) showSuccess(msg);
      else showWarning(msg);
    } catch {
      showError('Falha no teste de conexão');
    } finally {
      setTestingBilling(null);
      setTestingApi(null);
    }
  };

  const handleDelete = (p: ProductSummary) => {
    confirm({
      title: 'Excluir produto',
      message: `Remover "${p.displayName}" do registry?`,
      confirmText: 'Excluir',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await apiService.deleteProduct(p.productId);
          showSuccess('Produto removido');
          await load();
        } catch {
          showError('Erro ao excluir produto');
        }
      }
    });
  };

  const handleSave = async () => {
    const appApi = form.appApi.trim();
    const productUrl = form.productUrl.trim();
    if (appApi && !/^https?:\/\//i.test(appApi)) {
      showError('URL da API deve começar com https://');
      return;
    }
    if (productUrl && !/^https?:\/\//i.test(productUrl)) {
      showError('URL do produto deve começar com https://');
      return;
    }
    const displayName = form.displayName.trim();
    if (!displayName) {
      showError('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      if (!editingId) {
        const res = await apiService.onboardProduct({
          displayName,
          avatarUrl: form.avatarUrl.trim() || undefined,
          productUrl: productUrl || undefined,
          status: form.status || 'beta',
          type: form.type,
          isFree: form.isFree,
          urls: {
            appApi: appApi || undefined,
            ...(form.appApiSecret.trim() ? { appApiSecret: form.appApiSecret.trim() } : {})
          },
          integrations: {
            billingEnabled: form.billingEnabled,
            impersonationEnabled: false,
            usesControlDb: false,
            ...(form.pricingSchema ? { pricingSchema: form.pricingSchema } : {})
          }
        });
        setChecklist(res.data?.checklist || []);
        showSuccess('Produto registrado');
      } else {
        await apiService.upsertProduct(editingId, {
          displayName,
          avatarUrl: form.avatarUrl.trim() || undefined,
          productUrl: productUrl || undefined,
          status: form.status,
          type: form.type,
          isFree: form.isFree,
          urls: {
            appApi: appApi || undefined,
            ...(form.appApiSecret.trim() ? { appApiSecret: form.appApiSecret.trim() } : {})
          },
          integrations: {
            billingEnabled: form.billingEnabled,
            ...(form.pricingSchema ? { pricingSchema: form.pricingSchema } : {})
          }
        });
        showSuccess('Produto atualizado');
      }
      setDialogOpen(false);
      await load();
    } catch {
      showError(editingId ? 'Erro ao atualizar' : 'Falha no cadastro');
    } finally {
      setSaving(false);
    }
  };

  const canManage = can('products:manage');

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Registry de produtos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cadastro central em <code>core.products</code>. URL e secret da API ficam em{' '}
                <code>urls</code> (secret criptografado no banco).
              </Typography>
            </Box>
            {canManage && (
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                Adicionar produto
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map(p => (
              <TableRow key={p.productId}>
                <TableCell>
                  <ProductCell
                    productId={p.productId}
                    name={p.displayName}
                    avatarUrl={p.avatarUrl}
                  />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={p.status || 'active'} />
                </TableCell>
                <TableCell>
                  {p.isFree ? (
                    <Chip size="small" color="success" label="gratuito" />
                  ) : p.integrations?.billingEnabled !== false ? (
                    'sim'
                  ) : (
                    'não'
                  )}
                </TableCell>
                <TableCell align="right">
                  {canManage && (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {!p.isFree && (
                        <Button
                          size="small"
                          disabled={testingBilling === p.productId}
                          onClick={() => runTest(p.productId, 'billing')}
                        >
                          Testar billing
                        </Button>
                      )}
                      <Button
                        size="small"
                        disabled={testingApi === p.productId}
                        onClick={() => runTest(p.productId, 'appApi')}
                      >
                        Testar API
                      </Button>
                      <IconButton size="small" onClick={() => openEdit(p)} title="Editar">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(p)}
                        title="Excluir"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum produto cadastrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar produto' : 'Adicionar produto'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              URL e secret da API interna ficam no registry (<code>urls.appApi</code>,{' '}
              <code>urls.appApiSecret</code> criptografado). O backend do produto deve usar o mesmo
              secret (ex.: variável <code>VOVER_APP_API_SECRET</code> no vover-app).
            </Alert>
            <TextField
              label="Nome exibido"
              value={form.displayName}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
            />
            <TextField
              label="URL pública do produto"
              placeholder="https://www.produto.com.br"
              value={form.productUrl}
              onChange={e => setForm(f => ({ ...f, productUrl: e.target.value }))}
              helperText="Endereço público usado para acesso às informações comerciais do produto."
            />
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    {...(form.avatarUrl ? { src: form.avatarUrl } : {})}
                    sx={{ width: 64, height: 64, fontWeight: 700 }}
                  >
                    {(form.displayName || 'P').slice(0, 2).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Ícone/avatar do produto</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Carregue a imagem e ajuste o recorte. Se ficar vazio, o avatar mostra as
                      iniciais do produto.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => setAvatarEditorOpen(true)}
                  >
                    {form.avatarUrl ? 'Editar imagem' : 'Carregar imagem'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="beta">beta</MenuItem>
              <MenuItem value="deprecated">deprecated</MenuItem>
            </TextField>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isFree}
                    onChange={e => {
                      const isFree = e.target.checked;
                      // Produto gratuito nunca cobra (o backend também força billing desligado);
                      // ao deixar de ser gratuito, volta ao padrão de produto pago (billing ligado).
                      setForm(f => ({ ...f, isFree, billingEnabled: !isFree }));
                    }}
                  />
                }
                label="Produto gratuito"
              />
              <Typography variant="body2" color="text.secondary">
                Sem período de teste e sem cobrança. O login não passa pela verificação de
                trial/billing no vover-auth.
              </Typography>
            </Box>
            <TextField
              select
              label="Schema de precificação"
              value={form.pricingSchema}
              onChange={e => setForm(f => ({ ...f, pricingSchema: e.target.value }))}
              helperText="Define qual formulário de precificação este produto usa."
            >
              <MenuItem value="">Padrão / V-Condo (nenhum schema específico)</MenuItem>
              {Object.entries(PRICING_SCHEMA_REGISTRY).map(([key, entry]) => (
                <MenuItem key={key} value={key}>
                  {entry.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="URL base da API do produto"
              value={form.appApi}
              onChange={e => setForm(f => ({ ...f, appApi: e.target.value }))}
            />
            <TextField
              label="Secret da API do produto"
              type="password"
              autoComplete="new-password"
              helperText={
                editingId && form.appApiSecretConfigured
                  ? 'Deixe em branco para manter o secret atual. Mesmo valor configurado no backend do produto.'
                  : 'Gravado criptografado em urls.appApiSecret. Use o mesmo valor no backend do produto.'
              }
              value={form.appApiSecret}
              onChange={e => setForm(f => ({ ...f, appApiSecret: e.target.value }))}
            />
          </Stack>
          {checklist.length > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {checklist.map((line, i) => (
                <Typography key={i} variant="body2">
                  • {line}
                </Typography>
              ))}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {editingId ? 'Salvar' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
      <AvatarEditor
        open={avatarEditorOpen}
        onClose={() => setAvatarEditorOpen(false)}
        currentAvatar={form.avatarUrl}
        subjectName={form.displayName}
        title="Editar avatar do produto"
        onSave={avatar => setForm(f => ({ ...f, avatarUrl: avatar || '' }))}
      />
    </Box>
  );
};
